const Common = require("./CommonHelpers");
const {parseCoordinates} = require("../../../../shared/GenUtil");
const {getBoundingRect} = require("../../util/Math2D");

/**
 * Draws a division network with the user-provided puzzleData. This is a puzzle
 * type meant to teach divisors and remainders, see the BaTeX
 * guide for more information.
 *
 * @param {Object} ctx canvas context
 * @param {Array<Array<string>>} edgeData array of arrays defining the puzzle edges.
 *  Each array represents an edge, and is an array of the form
 *  ["from":"(x,y)", "to":"(x,y)", "value":"typeNumber" (optional), "color":"c"(optional), "isDirectional":"d"(optional)]
 *  "from" and "to" are strings of the form "(x,y)" representing the coordinates of the nodes the edge connects,
 *  "value" is a string of the form "typeNumber" where "type" is the type of edge, which can be "q" for a quotient edge, "r" for a remainder edge,
 *  or "qz" for a quotient edge that has to have remainder 0, and "number" is the number on the edge.
 *  The optional "color" is a string that is just the color name, e.g. "red" or "blue", used to color the edge.
 *  The optional "isDirectional" is a boolean that indicates whether the edge is directional.
 *  If it is directional, then it will be an arrow, otherwise it will be a line.
 * @param {Array<Array<string>>} nodeData array of arrays defining the nodes.
 *  Each array represents a node, and is an array of the form
 *  ["position":"(x,y)", "number":n(optional), "color":"c"(optional)]
 *  where position are the coordinates of the node and "number" is the number value of that node.
 */
const drawDivisionNetwork = function(ctx, edgeData, nodeData) {
	// Diameter of individual circles.
	const DIAMETER = 50;
	// Space between consecutive circles
	const LINE_LENGTH = 80;
	// Stroke width of the circles.
	const STROKE_WIDTH = 2;

	const diagramSize = ctx.getSize();
	const w = diagramSize.width;
	const h = diagramSize.height;

	// Parse puzzle data into nodes
	const correctedEdgeData = edgeData.map(formatStringArrayIntoEdgeObject);
	const correctedNodeData = nodeData.map(formatStringArrayIntoObject);

	const nodes = new Map(); // Map of "(x,y)" -> {x, y, number: null, color: null}

	// Parse node data into map
	for (const node of correctedNodeData) {
		const {position, number, color} = node;
		const [nodeX, nodeY] = parseCoordinates(position);
		nodes.set(position, {
			x: nodeX,
			y: nodeY,
			number: number || null,
			color: color || "black",
		});
	}

	// Parse edges
	for (const edgeObj of correctedEdgeData) {
		// Destructure the edge object into its parts we need
		const {from, to} = edgeObj;

		// Parse coordinates
		const [fromX, fromY] = parseCoordinates(from);
		const [toX, toY] = parseCoordinates(to);

		// Add nodes if they don't exist
		if (!nodes.has(from)) {
			nodes.set(from, {
				x: fromX,
				y: fromY,
				number: null,
				color: "black",
			});
		}
		if (!nodes.has(to)) {
			nodes.set(to, {
				x: toX,
				y: toY,
				number: null,
				color: "black",
			});
		}
	}

	// Calculate bounds for centering
	const nodePoints = Array.from(nodes.values()).map(node => [node.x, node.y]);
	const [minX, minY, width, height] = getBoundingRect(nodePoints);
	const maxX = minX + width;
	const maxY = minY + height;

	ctx.save();

	// Center the diagram
	const totalWidth = (maxX - minX + 1) * DIAMETER + (maxX - minX) * LINE_LENGTH;
	const totalHeight =
		(maxY - minY + 1) * DIAMETER + (maxY - minY) * LINE_LENGTH;

	// This centers the puzzle in the canvas. In particular, we
	// perform four translations:
	// 1. Shift everything to the right so that the smallest offset becomes 0.
	ctx.translate(
		-minX * (DIAMETER + LINE_LENGTH),
		-minY * (DIAMETER + LINE_LENGTH)
	);
	// 2. Shift right and down to account for the fact that circles are being
	// drawn at their center. After this, the puzzle is being drawn so it
	// exactly touches the top-left of the canvas.
	ctx.translate(DIAMETER / 2 + STROKE_WIDTH, DIAMETER / 2 + STROKE_WIDTH);
	// 3. Shift right to center the puzzle horizontally.
	ctx.translate((w - totalWidth) / 2, 0);
	// 4. Shift down to center the puzzle vertically.
	ctx.translate(0, (h - totalHeight) / 2);

	// Draw edges
	for (const edge of correctedEdgeData) {
		const fromNode = nodes.get(edge.from);
		const toNode = nodes.get(edge.to);

		// Calculate positions with constant spacing
		const fromX = fromNode.x * (DIAMETER + LINE_LENGTH);
		const fromY = (maxY - fromNode.y) * (DIAMETER + LINE_LENGTH);
		const toX = toNode.x * (DIAMETER + LINE_LENGTH);
		const toY = (maxY - toNode.y) * (DIAMETER + LINE_LENGTH);

		// get edge direction and positions taking node diameter into account
		const angle = Math.atan2(toY - fromY, toX - fromX);
		const startX = fromX + (Math.cos(angle) * DIAMETER) / 2;
		const startY = fromY + (Math.sin(angle) * DIAMETER) / 2;
		const endX = toX - (Math.cos(angle) * DIAMETER) / 2;
		const endY = toY - (Math.sin(angle) * DIAMETER) / 2;

		drawEdge(ctx, startX, startY, endX, endY, edge);

		// Draw arrowhead
		if (edge.isDirectional) {
			drawTriangle(
				ctx,
				(4 * startX + endX) / 5,
				(4 * startY + endY) / 5,
				12,
				angle,
				edge.color
			);
		}

		// Draw edge label
		const edgeDirection = getEdgeDirection(angle);
		drawEdgeLabel(ctx, startX, startY, endX, endY, edgeDirection, edge);
	}

	// Sort nodes by y value, then x value
	const sortedNodes = Array.from(nodes.values()).sort((a, b) => {
		// Sort by highest y value first (top to bottom)
		if (a.y !== b.y) {
			return b.y - a.y;
		}
		// Then by lowest x value (left to right)
		return a.x - b.x;
	});

	// Draw nodes
	for (const node of sortedNodes) {
		const nodeX = node.x * (DIAMETER + LINE_LENGTH);
		const nodeY = (maxY - node.y) * (DIAMETER + LINE_LENGTH);

		ctx.save();
		ctx.beginPath();
		ctx.arc(nodeX, nodeY, DIAMETER / 2, 0, 2 * Math.PI, false);
		ctx.lineWidth = STROKE_WIDTH;
		ctx.strokeStyle = node.color || "black";
		ctx.stroke();
		ctx.fillStyle = "white";
		ctx.fill();

		// Draw node number or blank
		ctx.fillStyle = node.color || "black";
		const batex = node.number == null ? `$\\blank{2}$` : node.number.toString();
		Common.drawBatexAtCenter(ctx, batex, nodeX, nodeY);
		ctx.restore();
	}

	ctx.restore();
};

const formatStringArrayIntoObject = array => {
	const obj = {};
	array.forEach(prop => {
		const [key, value] = prop.split(":");
		obj[key] = value;
	});

	return obj;
};

const formatStringArrayIntoEdgeObject = array => {
	const {from, to, value, color, isDirectional} = formatStringArrayIntoObject(
		array
	);
	let type = null;
	let number = null;
	if (value) {
		({type, number} = formatValueIntoTypeAndNumber(value));
	}
	return {from, to, type, number, color, isDirectional};
};

const formatValueIntoTypeAndNumber = value => {
	const parts = value.match(/^([a-zA-Z]+)(\d+)$/);
	const type = parts[1];
	const number = parts[2];
	return {type, number};
};

const getEdgeDirection = angle => {
	// edge direction of "vertical", "horizontal", "diagonal/", "diagonal\\"
	// where diagonal/ is bottom left to top right and diagonal\\ is bottom right to top left
	const normalizedAngle = (angle + 2 * Math.PI) % (2 * Math.PI);
	if (
		Math.abs(normalizedAngle - Math.PI / 2) < Math.PI / 4 ||
		Math.abs(normalizedAngle - (3 * Math.PI) / 2) < Math.PI / 4
	) {
		return "vertical";
	}
	// Horizontal edges (roughly)
	else if (
		Math.abs(normalizedAngle) < Math.PI / 4 ||
		Math.abs(normalizedAngle - Math.PI) < Math.PI / 4
	) {
		return "horizontal";
	}
	// Diagonal edge bottom right to top left
	else if (
		Math.abs(normalizedAngle - (3 * Math.PI) / 4) < Math.PI / 4 ||
		Math.abs(normalizedAngle - (7 * Math.PI) / 4) < Math.PI / 4
	) {
		return "diagonal/";
	} else {
		return "diagonal\\";
	}
};

const drawEdge = (ctx, startX, startY, endX, endY, edge) => {
	// Draw edge (thicker if it's a qz edge to put a white line on top)
	ctx.save();
	ctx.beginPath();
	ctx.moveTo(startX, startY);
	ctx.lineTo(endX, endY);
	ctx.strokeStyle = edge.color || "black";
	ctx.lineWidth = edge.type === "qz" ? 5 : 1;
	ctx.stroke();

	// Draw white line on top of qz edge to make double black line
	if (edge.type === "qz") {
		ctx.save();
		ctx.beginPath();
		ctx.moveTo(startX, startY);
		ctx.lineTo(endX, endY);
		ctx.strokeStyle = "white";
		ctx.lineWidth = 3;
		ctx.stroke();
	}
};

const drawTriangle = (ctx, topX, topY, size, angle, color) => {
	const arrowAngle = Math.PI / 6;
	ctx.save();
	ctx.beginPath();
	ctx.moveTo(topX, topY);
	ctx.lineTo(
		topX + size * Math.cos(angle - arrowAngle),
		topY + size * Math.sin(angle - arrowAngle)
	);
	ctx.lineTo(
		topX + size * Math.cos(angle + arrowAngle),
		topY + size * Math.sin(angle + arrowAngle)
	);
	ctx.lineTo(topX, topY);

	ctx.fillStyle = color || "black";
	ctx.fill();
	ctx.restore();
};

const drawEdgeLabel = (
	ctx,
	startX,
	startY,
	endX,
	endY,
	edgeDirection,
	edge
) => {
	// Draw edge label
	const labelX = (startX + endX) / 2;
	const labelY = (startY + endY) / 2;

	// Adjust label position based on angle
	const labelOffset = 12; // Distance from edge
	let offsetX = 0;
	let offsetY = 0;

	// Vertical edges (roughly)
	if (edgeDirection === "vertical") {
		offsetX = -labelOffset;
	}
	// Horizontal edges (roughly)
	else if (edgeDirection === "horizontal") {
		offsetY = -labelOffset;
	}
	// Diagonal edge bottom right to top left
	else if (edgeDirection === "diagonal/") {
		offsetX = (-2 * labelOffset) / 3;
		offsetY = (-2 * labelOffset) / 3;
	} else if (edgeDirection === "diagonal\\") {
		offsetX = (2 * labelOffset) / 3;
		offsetY = (-2 * labelOffset) / 3;
	}

	if (edge.type && edge.number) {
		// Draw edge label
		ctx.fillStyle = edge.color || "black";
		ctx.font = "12px Roboto";
		const label =
			edge.type === "r"
				? `$\\text{r}${edge.number}$`
				: `$\\text{q}${edge.number}$`;
		Common.drawBatexAtCenter(ctx, label, labelX + offsetX, labelY + offsetY);
		ctx.restore();
	}
};

module.exports = drawDivisionNetwork;
